<template>
  <div class="d-profile-bookshelf-list">
    <div
      v-if="type === 'reviewed'"
      v-once
    >
      <h2>Published reviews</h2>
    </div>

    <div
      v-else
      class="grid-flex grid-middle"
    >
      <div
        v-once
        class="grid-item"
      >
        <div
          v-if="isSelf"
          class="grid-flex grid-middle grid-no-response"
        >
          <div class="grid-item-shrink">
            <h2>
              My library
            </h2>
          </div>

          <div
            v-if="!type"
            v-once
            class="grid-item space-left-1em"
          >
            <a
              :href="bookshelfUrl"
              title="Manage"
            >
              Manage
            </a>
          </div>
        </div>

        <h2 v-else>
          {{ userName }}'s library
        </h2>
      </div>

      <div
        v-if="!type"
        v-once
        class="grid-item-shrink"
      >
        <DuiDropdown
          v-model="bookshelf"
          class="bookshelf-dropdown"
          :disabled="isLoading"
          :options="bookshelfOptions"
        />
      </div>
    </div>

    <div v-if="isInitialized">
      <div
        v-if="!entries.length"
        class="no-entries text-center padding-top-8em padding-bottom-5em"
      >
        <!-- No Reviews -->
        <div v-if="type === 'reviewed'">
          <img
            alt="No books yet"
            class="space-bottom-3em"
            :src="emptyBookshelfSrc"
          >

          <div v-if="isSelf">
            <h2>
              Review your favorite books!
            </h2>

            <p class="large">
              Review any book on Discovery, Amazon or Google Play.
            </p>
          </div>

          <div v-else>
            <h2>
              No reviews yet.
            </h2>

            <p class="large">
              Come back later to check {{ userName }}'s reviews.
            </p>
          </div>

          <img
            alt="No books yet"
            class="space-top-3em"
            :src="bookshelfSkeletonSrc"
          >
        </div>
        <!-- END No Reviews -->

        <!-- No Bookshelf entries -->
        <div v-else>
          <img
            alt="No books yet"
            class="space-bottom-2em"
            :src="emptyBookshelfSrc"
          >

          <div v-if="isSelf">
            <h2>
              Your bookshelf is empty!
            </h2>

            <p class="large space-bottom-2em">
              Track your reading by adding books to your bookshelf.
            </p>

            <a
              class="button-yellow button"
              :href="bookshelfUrl"
              title="Open bookshelf"
            >
              Open bookshelf
            </a>
          </div>

          <div v-else>
            <h2>
              Empty Bookshelf
            </h2>
            <p class="large">
              Come back later to check {{ userName }}'s bookshelf.
            </p>
          </div>
        </div>
        <!-- END No Bookshelf entries -->
      </div>

      <div
        v-else
        class="books-list"
      >
        <DProfileBookshelfListEntry
          v-for="entry in entries"
          :key="entry.slug"
          :entry="entry"
        />
      </div>

      <div ref="loadMoreBookshelfEntries" />
    </div>

    <div
      v-if="isLoading"
      class="loading-container"
    >
      <DuiLoadingIndicator size="lg" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import BOOKSHELF_SKELETON_IMG from '@d-assets/bookshelf-skeleton.svg';
import USER_REVIEWS_IMG from '@d-assets/user-reviews.svg';
import bookshelfService from '@d-services/bookshelf-service/bookshelf-service';
import URLS from '@d-shared/urls/urls';
import { getErrorMessage } from '@d-shared/utils/errors';
import { whenElementIsVisible } from '@dui/helpers/util-funcs';

import DProfileBookshelfListEntry from './d-profile-bookshelf-list-entry/d-profile-bookshelf-list-entry.vue';

export default {
  components: {
    DProfileBookshelfListEntry,
  },
  props: {
    isSelf: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: null,
    },
    userName: {
      type: String,
      default: null,
    },
    userId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    bookshelf: 'all',
    bookshelfOptions: bookshelfService.getBookshelvesOptions(true),
    bookshelfSkeletonSrc: BOOKSHELF_SKELETON_IMG,
    bookshelfUrl: URLS.app.library.bookshelf,
    disableLoadMore: null,
    emptyBookshelfSrc: USER_REVIEWS_IMG,
    entries: [],
    hasMoreResults: false,
    isInitialized: false,
    isLoading: false,
    nextPage: 1,
  }),
  watch: {
    bookshelf: function() {
      this.searchBookshelf();
    },
  },
  beforeDestroy() {
    if (this.disableLoadMore) {
      this.disableLoadMore();
    }
  },
  mounted() {
    this.loadBookshelfEntries();
  },
  methods: {
    configureLoadMore() {
      if (!this.isLoading && this.hasMoreResults) {
        this.enableLoadMore();
        return;
      }

      if (this.disableLoadMore) {
        this.disableLoadMore();
      }
    },

    async enableLoadMore() {
      if (this.disableLoadMore) {
        this.disableLoadMore();
      }

      // loadMoreBookshelfEntries element is conditionally rendered
      // according to the existence of results
      await this.$nextTick();

      if (!this.$refs.loadMoreBookshelfEntries) {
        return;
      }

      const disableLoadMore = whenElementIsVisible(
        this.$refs.loadMoreBookshelfEntries,
        this.loadBookshelfEntries.bind(this),
      );

      this.$set(this, 'disableLoadMore', disableLoadMore);
    },

    async loadBookshelfEntries() {
      this.$set(this, 'isLoading', true);

      try {
        const bookShelfResults = await bookshelfService.getUserEntries(
          this.userId,
          (this.type || this.bookshelf),
          this.nextPage,
        );

        this.$set(this, 'entries', this.entries.concat(bookShelfResults.bookshelf_entries));
        this.$set(this, 'hasMoreResults', !!bookShelfResults.meta.next_page);
        this.$set(this, 'nextPage', this.nextPage + 1);
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      if (!this.isInitialized) {
        this.$set(this, 'isInitialized', true);
      }

      this.$set(this, 'isLoading', false);
      this.configureLoadMore();
    },

    reset() {
      this.$set(this, 'entries', []);
      this.$set(this, 'hasMoreResults', false);
      this.$set(this, 'isInitialized', false);
      this.$set(this, 'isLoading', false);
      this.$set(this, 'nextPage', 1);
    },

    searchBookshelf() {
      this.reset();
      this.loadBookshelfEntries();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

.d-profile-bookshelf-list {
  .loading-container {
    padding: $_2em 0 $_3em;
    text-align: center;
  }

  .books-list .d-profile-bookshelf-list-entry {
    border-top: $border-width-base solid getColor($grey);

    &:first-child {
      border-top: none;
    }
  }

  @include screenMoreThan(xs) {
    .bookshelf-dropdown {
      width: 190px;
    }
  }
}
</style>
